import { Deal } from '../types';
import { z } from 'zod';
import { isLiveDeal } from '../components/deals/dealFilters';

// Dev builds read the dev feed written by local deals-processor runs.
const DEALS_URL = import.meta.env.DEV
  ? 'https://assets.share-a-cart.com/deals/deals-dev.json'
  : 'https://assets.share-a-cart.com/deals/deals.json';

const dealSchema: z.ZodType<Deal, z.ZodTypeDef, unknown> = z.object({
  retailer: z.string(),
  retailerDisplayName: z.string().nullish().transform(v => v ?? null),
  name: z.string(),
  description: z.string().nullable(),
  link: z.string(),
  code: z.string().nullable(),
  image_url: z.string().nullable(),
  featured: z.boolean(),
  // Built at ingestion for every deal. A deal without it cannot produce a tracked click, so it is
  // dropped below rather than rendered with a client-built URL.
  dealClickBase: z.string().min(1),
  school: z.boolean().optional().default(false),
  effective_at: z.string().nullable(),
  expires_at: z.string().nullable(),
});

const dealsEnvelopeSchema = z.object({
  deals: z.array(z.unknown()),
});

export const getDeals = async (): Promise<Deal[]> => {
  const res = await fetch(DEALS_URL);
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Error fetching deals: HTTP ${res.status}${body ? ` — ${body}` : ''}`);
  }
  const data = await res.json();
  // Per deal, not all-or-nothing: z.array(dealSchema) would throw on a single malformed entry and
  // blank /deals for everyone. Drop the bad one instead.
  return dealsEnvelopeSchema.parse(data).deals.flatMap((raw) => {
    const parsed = dealSchema.safeParse(raw);
    if (!parsed.success) return [];
    // Parked storefronts can still sit on the CDN; drop them by source, not `featured`.
    const source = raw && typeof raw === 'object' && 'source' in raw ? (raw as { source?: unknown }).source : undefined;
    if (source === 'storefront') return [];
    if (!isLiveDeal(parsed.data)) return [];
    return [parsed.data];
  });
};
